import LottoShell from "../components/lotto/LottoShell";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastStyles from "../utils/styles";
import { getApiErrorMessage } from "../utils/apiError";
import { getBankInfo, BankInfoResponse } from "../api";
import LoadingOverlay from "../components/LoadingOverlay";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DataLoader from "../components/DataLoader";
import { useCustomColors } from "../hooks/useCustomColors";

const BankInfoPage = () => {
  const colors = useCustomColors();
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [bankData, setBankData] = useState<BankInfoResponse | null>(null);

  // Inject custom toast styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = toastStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Fetch bank info on mount
  useEffect(() => {
    const fetchBankInfo = async () => {
      if (!transactionId) {
        toast.error("Transaction ID not found");
        navigate("/");
        return;
      }

      setIsLoading(true);
      try {
        const response = await getBankInfo(transactionId);
        if (response.status === 1) {
          setBankData(response.data);
        } else {
          toast.error(response.message || "Failed to fetch bank information");
          navigate("/");
        }
      } catch (error) {
        console.error("Fetch bank info error:", error);
        toast.error(getApiErrorMessage(error, "Failed to fetch bank information. Please try again."));
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBankInfo();
  }, [transactionId, navigate]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleBackToPayment = () => {
    navigate("/");
  };

  // Check if payment method is DuitNow
  const isDuitNow = bankData?.bank_info?.type === "duitnow";

  if (isLoading) {
    return (
      <LottoShell>
        <Box
          sx={{
            minHeight: "100vh",
            background:
              `radial-gradient(circle at center, ${colors.card.background} 0%, ${colors.background.page} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DataLoader />
        </Box>
      </LottoShell>
    );
  }

  return (
    <LottoShell>
      <Box
        sx={{
          minHeight: "100vh",
          background:
            `radial-gradient(circle at center, ${colors.card.background} 0%, ${colors.background.page} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 520,
            width: "100%",
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 1px rgba(255, 255, 255, 0.1)",
            border: `1px solid ${colors.background.panelSecondary}`,
            background: colors.isDark ? "#ffffff08" : colors.card.background,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: "16px",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "center",
                  mb: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    background: isDuitNow
                      ? colors.gradient.gold
                      : colors.gradient.gold,
                    boxShadow: isDuitNow
                      ? `0 4px 20px ${colors.border.accent}`
                      : `0 4px 20px ${colors.border.accent}`,
                    borderRadius: "8px",
                    mr: { xs: 0, md: 2 },
                    mb: { xs: 2, md: 0 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isDuitNow ? (
                    <PhoneAndroidIcon sx={{ color: "#FFFFFF", fontSize: 20 }} />
                  ) : (
                    <AccountBalanceIcon sx={{ color: "#FFFFFF", fontSize: 20 }} />
                  )}
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    background: isDuitNow
                      ? colors.gradient.gold
                      : colors.gradient.gold,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                  }}
                >
                  {isDuitNow ? "DuitNow Transfer" : "Bank Transfer Details"}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontSize: "14px" }}
              >
                {isDuitNow
                  ? "Please transfer the exact amount via DuitNow"
                  : "Please transfer the exact amount to the account below"}
              </Typography>
            </Box>

            <Stack spacing={3}>
              {/* Amount to Transfer */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Amount to Transfer
                </Typography>
                <Box
                  sx={{
                    backgroundColor: colors.card.background,
                    borderRadius: "8px",
                    border: `1px solid ${colors.border.accent}`,
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: colors.accent.gold,
                      fontSize: { xs: "28px", sm: "32px" },
                      fontWeight: "bold",
                    }}
                  >
                    MYR {bankData?.amount?.toFixed(2) || "0.00"}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.accent.danger,
                    fontSize: "11px",
                    mt: 0.5,
                    display: "block",
                    fontWeight: "500",
                  }}
                >
                  * Please transfer the EXACT amount shown above
                </Typography>
              </Box>

              {/* Bank Name - Only for Bank Transfer */}
              {!isDuitNow && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Bank Name
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: colors.card.background,
                      borderRadius: "8px",
                      border: `1px solid ${colors.border.accent}`,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      {bankData?.bank_info?.bank_name || "-"}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Account Name */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Account Name
                </Typography>
                <Box
                  sx={{
                    backgroundColor: colors.card.background,
                    borderRadius: "8px",
                    border: `1px solid ${colors.border.accent}`,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                  >
                    {bankData?.bank_info?.account_name || "-"}
                  </Typography>
                  <Tooltip title="Copy account name">
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          bankData?.bank_info?.account_name || "",
                          "Account name"
                        )
                      }
                      sx={{ color: colors.accent.gold }}
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Account Number - Only for Bank Transfer */}
              {!isDuitNow && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Account Number
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: colors.card.background,
                      borderRadius: "8px",
                      border: `1px solid ${colors.border.accent}`,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                        fontSize: "18px",
                        fontWeight: "bold",
                        fontFamily: "monospace",
                        letterSpacing: "2px",
                      }}
                    >
                      {bankData?.bank_info?.account_number || "-"}
                    </Typography>
                    <Tooltip title="Copy account number">
                      <IconButton
                        onClick={() =>
                          handleCopy(
                            bankData?.bank_info?.account_number || "",
                            "Account number"
                          )
                        }
                        sx={{ color: colors.accent.gold }}
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {/* DuitNow Phone Number - Only for DuitNow */}
              {isDuitNow && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    DuitNow Phone Number
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: colors.card.background,
                      borderRadius: "8px",
                      border: `1px solid ${colors.border.accent}`,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneAndroidIcon sx={{ color: colors.accent.gold, fontSize: 20 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                          fontSize: "18px",
                          fontWeight: "bold",
                          fontFamily: "monospace",
                          letterSpacing: "2px",
                        }}
                      >
                        {bankData?.bank_info?.phone_no || "-"}
                      </Typography>
                    </Box>
                    <Tooltip title="Copy phone number">
                      <IconButton
                        onClick={() =>
                          handleCopy(
                            bankData?.bank_info?.phone_no || "",
                            "Phone number"
                          )
                        }
                        sx={{ color: colors.accent.gold }}
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {/* Reference Number */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Reference Number (Include in Transfer)
                </Typography>
                <Box
                  sx={{
                    backgroundColor: isDuitNow
                      ? colors.tint.gold
                      : colors.tint.primary,
                    borderRadius: "8px",
                    border: isDuitNow
                      ? `1px solid ${colors.border.strong}`
                      : `1px solid ${colors.border.strong}`,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDuitNow ? colors.accent.gold : colors.accent.gold,
                      fontSize: "16px",
                      fontWeight: "bold",
                      fontFamily: "monospace",
                    }}
                  >
                    {bankData?.reference_number || "-"}
                  </Typography>
                  <Tooltip title="Copy reference number">
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          bankData?.reference_number || "",
                          "Reference number"
                        )
                      }
                      sx={{ color: isDuitNow ? colors.accent.gold : colors.accent.gold }}
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.accent.danger,
                    fontSize: "11px",
                    mt: 0.5,
                    display: "block",
                    fontWeight: "500",
                  }}
                >
                  * IMPORTANT: Include this reference in your transfer description
                </Typography>
              </Box>

              {/* Instructions */}
              {bankData?.instructions && bankData.instructions.length > 0 && (
                <Box
                  sx={{
                    backgroundColor: isDuitNow
                      ? colors.tint.gold
                      : colors.tint.primary,
                    border: isDuitNow
                      ? `1px solid ${colors.border.accent}`
                      : `1px solid ${colors.tint.goldStrong}`,
                    borderRadius: "8px",
                    p: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDuitNow ? colors.accent.gold : colors.accent.gold,
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1.5,
                      display: "block",
                    }}
                  >
                    Instructions
                  </Typography>
                  <Stack spacing={1}>
                    {bankData.instructions.map((instruction, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            color: isDuitNow ? colors.accent.gold : colors.accent.gold,
                            fontSize: "14px",
                            fontWeight: "bold",
                            minWidth: "20px",
                          }}
                        >
                          {index + 1}.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                            fontSize: "14px",
                          }}
                        >
                          {instruction}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Action Buttons */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  onClick={handleBackToPayment}
                  variant="outlinedDark"
                  fullWidth
                  sx={{ py: 2 }}
                >
                  Back
                </Button>
              </Stack>
            </Stack>

            {/* Security Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 4,
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${colors.tint.goldStrong}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockIcon sx={{ color: colors.text.secondary, fontSize: "16px" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "11px",
                  }}
                >
                  SSL Secured
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <VerifiedUserIcon sx={{ color: colors.text.secondary, fontSize: "16px" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "11px",
                  }}
                >
                  PCI Compliant
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      <LoadingOverlay open={isLoading} message="Loading bank details..." />
    </LottoShell>
  );
};

export default BankInfoPage;
